"use client";

import type { UIMessage } from "ai";
import cx from "classnames";
import type React from "react";
import {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
  memo,
} from "react";
import { toast } from "sonner";
import { useLocalStorage, useWindowSize } from "usehooks-ts";

import { Button } from "@tracify/ui/button";
import equal from "fast-deep-equal";
import type { UseChatHelpers } from "@ai-sdk/react";
import {
  extractLatestRuntimeContextFromMessages,
  serializeRuntimeContextText,
  type AskUserQuestionResponse,
  type ChatRuntimeContext,
  type ChatSeedTags,
  type ChatMessage,
} from "@tracify/shared";
import { useScrollToBottom } from "@lib/ai/hooks/use-scroll-to-bottom";
import { useDashboards } from "@lib/api-hooks/useDashboards";
import { useInsightsLibrary } from "@lib/api-hooks/useInsightsLibrary";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { getDashboardMentionItems } from "@/lib/navigation/sidebar-items";
import { FeatureFlagUtils, useFeatureFlags } from "@/lib/hooks/useFeatureFlags";
import { ArrowUpIcon, PlusIcon, StopIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/solid";
import FilterChip from "@/components/Filter/FilterChip";
import { SuggestedActions } from "./suggested-actions";
import { ModelSelector } from "./model-selector";
import { ThinkingEffortSelector } from "./thinking-effort-selector";
import {
  getMentionQueryAtCaret,
  removeMentionQuery,
  type MentionKind,
} from "./mentions";
import { Popover, PopoverAnchor, PopoverContent } from "@tracify/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@tracify/ui/command";
import { AskUserQuestionPanel } from "./ask-user-question-panel";
import type { PendingAskUserQuestion } from "./ask-user-question";
import {
  CHAT_ROUTE_CONTEXT_PARAM,
  CHAT_ROUTE_QUERY_PARAM,
} from "@lib/ai/chat-entry-params";

const MAX_TAGS = 12;
const MIN_TEXTAREA_HEIGHT_PX = 24;
const DEFAULT_TEXTAREA_HEIGHT_PX = 48; // ~2 rows; used when clearing after send
const MAX_TEXTAREA_HEIGHT_PX = 220;
const CARET_KEYS = new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
  "PageUp",
  "PageDown",
]);

type MentionItem = {
  kind: MentionKind;
  id: string;
  label: string;
};

function PureMultimodalInput({
  input,
  setInput,
  status,
  stop,
  messages,
  setMessages,
  sendMessage,
  seedTags,
  chatId,
  seedInput,
  runtimeContext,
  onSeedTagsApplied,
  onSeedInputApplied,
  showSuggestedActions = true,
  pendingAskUserQuestion,
  isPendingAskUserQuestionSubmitting = false,
  onSubmitAskUserQuestion,
  className,
  isDraftRoute = false,
}: {
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  status: UseChatHelpers<ChatMessage>["status"];
  stop: () => void;
  chatId: string;
  messages: Array<UIMessage>;
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
  sendMessage: UseChatHelpers<ChatMessage>["sendMessage"];
  seedTags?: ChatSeedTags | null;
  seedInput?: string | null;
  runtimeContext: ChatRuntimeContext;
  onSeedTagsApplied?: () => void;
  onSeedInputApplied?: () => void;
  showSuggestedActions?: boolean;
  pendingAskUserQuestion?: PendingAskUserQuestion | null;
  isPendingAskUserQuestionSubmitting?: boolean;
  onSubmitAskUserQuestion?: (input: {
    toolCallId: string;
    output: AskUserQuestionResponse;
  }) => void;
  className?: string;
  isDraftRoute?: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const adjustRafId = useRef<number | null>(null);
  const observedInputContainerWidthRef = useRef<number | null>(null);
  const hasHydratedRef = useRef(false);
  const { width } = useWindowSize();
  const { connectedChannels } = useSelectedStores();
  const { flags } = useFeatureFlags();
  const isCustomInsightsEnabled =
    FeatureFlagUtils.isCustomInsightsEnabled(flags);
  const { data: dashboards = [], isLoading: areDashboardsLoading } =
    useDashboards({
      enabled: isCustomInsightsEnabled,
    });
  const { data: insights = [], isLoading: areInsightsLoading } =
    useInsightsLibrary({
      enabled: isCustomInsightsEnabled,
    });
  const [taggedDashboards, setTaggedDashboards] = useState<MentionItem[]>([]);
  const [taggedInsights, setTaggedInsights] = useState<MentionItem[]>([]);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionMode, setMentionMode] = useState<"textarea" | "command">(
    "textarea"
  );
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionEnd, setMentionEnd] = useState<number | null>(null);
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0);
  const commandInputRef = useRef<HTMLInputElement>(null);

  const adjustHeight = useCallback(() => {
    if (!textareaRef.current) return;
    if (adjustRafId.current !== null) {
      cancelAnimationFrame(adjustRafId.current);
    }
    adjustRafId.current = requestAnimationFrame(() => {
      const element = textareaRef.current;
      if (!element) return;
      element.style.height = "auto";
      const nextHeight = Math.min(
        Math.max(element.scrollHeight + 2, MIN_TEXTAREA_HEIGHT_PX),
        MAX_TEXTAREA_HEIGHT_PX
      );
      element.style.height = `${nextHeight}px`;
      element.style.overflowY =
        element.scrollHeight + 2 > MAX_TEXTAREA_HEIGHT_PX ? "auto" : "hidden";
    });
  }, []);

  useEffect(() => {
    const inputContainer = inputContainerRef.current;
    if (!inputContainer || typeof ResizeObserver === "undefined") return;

    observedInputContainerWidthRef.current = Math.round(
      inputContainer.getBoundingClientRect().width
    );

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const nextWidth = Math.round(entry.contentRect.width);
      if (nextWidth === observedInputContainerWidthRef.current) return;
      observedInputContainerWidthRef.current = nextWidth;
      adjustHeight();
    });

    resizeObserver.observe(inputContainer);

    return () => resizeObserver.disconnect();
  }, [adjustHeight]);

  useEffect(() => {
    return () => {
      if (adjustRafId.current !== null) {
        cancelAnimationFrame(adjustRafId.current);
      }
    };
  }, []);

  const resetHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${DEFAULT_TEXTAREA_HEIGHT_PX}px`;
      textareaRef.current.style.overflowY = "hidden";
    }
  }, []);

  const [localStorageInput, setLocalStorageInput] = useLocalStorage(
    "input",
    ""
  );
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasHydratedRef.current) return;

    // localStorageInput may be "" during SSR/hydration (usehooks-ts returns
    // the default first, then the stored value on the next render).  Wait for
    // a non-empty value before restoring; if nothing is stored, there's
    // nothing to hydrate.
    if (!localStorageInput) return;

    hasHydratedRef.current = true;
    setInput(localStorageInput);
    adjustHeight();
  }, [adjustHeight, localStorageInput, setInput]);

  useEffect(() => {
    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
    }
    persistTimeoutRef.current = setTimeout(() => {
      setLocalStorageInput(input);
    }, 300);
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
    };
  }, [input, setLocalStorageInput]);

  const builtinDashboardItems = useMemo(() => {
    return getDashboardMentionItems(connectedChannels);
  }, [connectedChannels]);
  const builtinDashboardIds = useMemo(() => {
    return new Set(builtinDashboardItems.map((dashboard) => dashboard.id));
  }, [builtinDashboardItems]);

  const dashboardMentionItems = useMemo<MentionItem[]>(() => {
    const regularDashboards = builtinDashboardItems.map(
      (dashboard) =>
        ({
          kind: "dash",
          id: dashboard.id,
          label: dashboard.label,
        }) satisfies MentionItem
    );
    const customDashboards = isCustomInsightsEnabled
      ? dashboards.map(
          (dashboard) =>
            ({
              kind: "dash",
              id: dashboard.id,
              label: dashboard.title,
            }) satisfies MentionItem
        )
      : [];
    return [...regularDashboards, ...customDashboards];
  }, [builtinDashboardItems, dashboards, isCustomInsightsEnabled]);

  const insightMentionItems = useMemo<MentionItem[]>(() => {
    if (!isCustomInsightsEnabled) {
      return [];
    }

    return insights.map((insight) => ({
      kind: "insight",
      id: insight.id,
      label: insight.title,
    }));
  }, [insights, isCustomInsightsEnabled]);

  const mentionItems = useMemo<MentionItem[]>(() => {
    return [...dashboardMentionItems, ...insightMentionItems];
  }, [dashboardMentionItems, insightMentionItems]);
  const availableMentionKeys = useMemo(() => {
    return new Set(mentionItems.map((item) => `${item.kind}:${item.id}`));
  }, [mentionItems]);
  const taggedDashboardsForContext = useMemo(
    () =>
      taggedDashboards.filter(
        (item) => isCustomInsightsEnabled || builtinDashboardIds.has(item.id)
      ),
    [builtinDashboardIds, isCustomInsightsEnabled, taggedDashboards]
  );
  const taggedInsightsForContext = useMemo(() => {
    return taggedInsights;
  }, [taggedInsights]);

  const selectedMentions = useMemo(() => {
    return [...taggedDashboardsForContext, ...taggedInsightsForContext].slice(
      0,
      MAX_TAGS
    );
  }, [taggedDashboardsForContext, taggedInsightsForContext]);
  const taggedForRuntimeContext = useMemo(() => {
    return selectedMentions.map((item) => ({
      id: item.id,
      type:
        item.kind === "dash" ? ("dashboard" as const) : ("insight" as const),
      label: item.label,
    }));
  }, [selectedMentions]);
  const runtimeContextPayload = useMemo(
    () =>
      ({
        ...runtimeContext,
        ...(taggedForRuntimeContext.length > 0
          ? { tagged: taggedForRuntimeContext }
          : {}),
      }) as const,
    [runtimeContext, taggedForRuntimeContext]
  );
  const runtimeContextText = useMemo(
    () => serializeRuntimeContextText(runtimeContextPayload),
    [runtimeContextPayload]
  );
  const runtimeContextTextPart = useMemo(
    () =>
      ({
        type: "text" as const,
        text: runtimeContextText,
      }) as const,
    [runtimeContextText]
  );
  const shouldIncludeRuntimeContextTextPart = useMemo(() => {
    const latestRuntimeContext =
      extractLatestRuntimeContextFromMessages(messages);
    if (!latestRuntimeContext) {
      return true;
    }

    return (
      serializeRuntimeContextText(latestRuntimeContext) !== runtimeContextText
    );
  }, [messages, runtimeContextText]);

  useEffect(() => {
    if (!seedTags) return;

    const seededItems: MentionItem[] = (seedTags.tagged ?? []).map((item) => ({
      kind:
        item.type === "dashboard" ? ("dash" as const) : ("insight" as const),
      id: item.id,
      label: item.label,
    }));
    const hasPendingSeededItems = seededItems.some((item) => {
      const key = `${item.kind}:${item.id}`;
      if (availableMentionKeys.has(key)) {
        return false;
      }

      if (item.kind === "dash") {
        return areDashboardsLoading;
      }

      return isCustomInsightsEnabled && areInsightsLoading;
    });

    if (hasPendingSeededItems) {
      return;
    }

    const deduped: MentionItem[] = [];
    const seen = new Set<string>();
    for (const item of seededItems) {
      const key = `${item.kind}:${item.id}`;
      const canApplyMissingSeededInsight =
        item.kind === "insight" && !isCustomInsightsEnabled;
      if (seen.has(key)) continue;
      if (!availableMentionKeys.has(key) && !canApplyMissingSeededInsight) {
        continue;
      }
      seen.add(key);
      deduped.push(item);
    }

    const limited = deduped.slice(0, MAX_TAGS);
    const nextTaggedDashboards = limited.filter((item) => item.kind === "dash");
    const nextTaggedInsights = limited.filter(
      (item) => item.kind === "insight"
    );
    setTaggedDashboards((currentTaggedDashboards) =>
      equal(currentTaggedDashboards, nextTaggedDashboards)
        ? currentTaggedDashboards
        : nextTaggedDashboards
    );
    setTaggedInsights((currentTaggedInsights) =>
      equal(currentTaggedInsights, nextTaggedInsights)
        ? currentTaggedInsights
        : nextTaggedInsights
    );

    setMentionOpen(false);
    setMentionQuery("");
    setMentionMode("textarea");
    setMentionStart(null);
    setMentionEnd(null);
    setMentionActiveIndex(0);

    onSeedTagsApplied?.();
  }, [
    areDashboardsLoading,
    areInsightsLoading,
    availableMentionKeys,
    isCustomInsightsEnabled,
    onSeedTagsApplied,
    seedTags,
  ]);

  useEffect(() => {
    if (!seedInput) return;

    const normalizedSeedInput = seedInput.trim();
    if (!normalizedSeedInput) {
      onSeedInputApplied?.();
      return;
    }

    setInput(normalizedSeedInput);
    setLocalStorageInput(normalizedSeedInput);
    adjustHeight();
    requestAnimationFrame(() => textareaRef.current?.focus());
    onSeedInputApplied?.();
  }, [
    adjustHeight,
    onSeedInputApplied,
    seedInput,
    setInput,
    setLocalStorageInput,
  ]);

  useEffect(() => {
    setTaggedDashboards((current) => {
      const filtered = current.filter(
        (item) => isCustomInsightsEnabled || builtinDashboardIds.has(item.id)
      );
      return filtered.length === current.length ? current : filtered;
    });
  }, [builtinDashboardIds, isCustomInsightsEnabled]);

  const updateMentionState = useCallback(
    (value: string, caretIndex: number) => {
      const queryInfo = getMentionQueryAtCaret(value, caretIndex);
      if (!queryInfo) {
        setMentionOpen(false);
        setMentionQuery("");
        setMentionMode("textarea");
        setMentionStart(null);
        setMentionEnd(null);
        return;
      }

      if (selectedMentions.length >= MAX_TAGS) {
        setMentionOpen(false);
        setMentionQuery("");
        setMentionMode("textarea");
        setMentionStart(null);
        setMentionEnd(null);
        return;
      }

      setMentionOpen(true);
      setMentionQuery(queryInfo.query);
      setMentionMode("textarea");
      setMentionStart(queryInfo.start);
      setMentionEnd(queryInfo.end);
    },
    [selectedMentions.length]
  );

  const openMentionPicker = useCallback(() => {
    setMentionOpen(true);
    setMentionQuery("");
    setMentionMode("command");
    setMentionStart(null);
    setMentionEnd(null);
    setMentionActiveIndex(0);
    requestAnimationFrame(() => commandInputRef.current?.focus());
  }, []);

  const filteredMentions = useMemo(() => {
    if (!mentionOpen) return [];
    const rawQuery = mentionQuery.trim();
    const lowerQuery = rawQuery.toLowerCase();
    let kindFilter: "dash" | "insight" | null = null;
    let queryText = rawQuery;

    if (lowerQuery.startsWith("dash:")) {
      kindFilter = "dash";
      queryText = rawQuery.slice(5);
    } else if (lowerQuery.startsWith("insight:")) {
      kindFilter = "insight";
      queryText = rawQuery.slice(8);
    }

    const normalizedQuery = queryText.trim().toLowerCase();

    return mentionItems.filter((item) => {
      if (kindFilter && item.kind !== kindFilter) return false;
      if (!normalizedQuery) return true;
      return (
        item.label.toLowerCase().includes(normalizedQuery) ||
        item.id.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [mentionItems, mentionOpen, mentionQuery]);

  const indexedMentions = useMemo(() => {
    return filteredMentions.map((item, index) => ({
      ...item,
      index,
    }));
  }, [filteredMentions]);

  const filteredDashMentions = useMemo(() => {
    return indexedMentions.filter((item) => item.kind === "dash");
  }, [indexedMentions]);

  const filteredInsightMentions = useMemo(() => {
    return indexedMentions.filter((item) => item.kind === "insight");
  }, [indexedMentions]);

  const safeMentionActiveIndex =
    filteredMentions.length > 0
      ? Math.min(mentionActiveIndex, filteredMentions.length - 1)
      : 0;

  const { scrollToBottom } = useScrollToBottom();

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    setInput(nextValue);
    adjustHeight();
    updateMentionState(
      nextValue,
      event.target.selectionStart ?? nextValue.length
    );
  };

  const submitForm = useCallback(() => {
    if (isDraftRoute) {
      const nextUrl = new URL(window.location.href);
      nextUrl.pathname = `/chat/${chatId}`;
      nextUrl.searchParams.delete(CHAT_ROUTE_QUERY_PARAM);
      nextUrl.searchParams.delete(CHAT_ROUTE_CONTEXT_PARAM);
      window.history.pushState(
        {},
        "",
        `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
      );
    }

    const textPart = {
      type: "text" as const,
      text: input,
    };
    const message = {
      role: "user" as const,
      parts: shouldIncludeRuntimeContextTextPart
        ? [textPart, runtimeContextTextPart]
        : [textPart],
    };

    void sendMessage(message);

    setLocalStorageInput("");
    resetHeight();
    setInput("");
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setMentionEnd(null);
    setMentionActiveIndex(0);
    scrollToBottom();

    if (width && width > 768) {
      textareaRef.current?.focus();
    }
  }, [
    input,
    chatId,
    runtimeContextTextPart,
    shouldIncludeRuntimeContextTextPart,
    setInput,
    sendMessage,
    setLocalStorageInput,
    width,
    resetHeight,
    scrollToBottom,
    isDraftRoute,
  ]);

  const handleMentionSelect = useCallback(
    (item: MentionItem) => {
      if (selectedMentions.length >= MAX_TAGS) {
        toast.error("You can only tag up to 12 items.");
        return;
      }

      const caretIndex = textareaRef.current?.selectionStart ?? input.length;
      const queryInfo =
        mentionStart !== null && mentionEnd !== null
          ? { start: mentionStart, end: mentionEnd }
          : getMentionQueryAtCaret(input, caretIndex);

      const nextValue = queryInfo
        ? removeMentionQuery(input, queryInfo.start, queryInfo.end)
        : input;

      setInput(nextValue);
      adjustHeight();
      setMentionOpen(false);
      setMentionQuery("");
      setMentionStart(null);
      setMentionEnd(null);

      if (item.kind === "dash") {
        setTaggedDashboards((current) => {
          if (current.some((existing) => existing.id === item.id)) {
            return current;
          }
          return [...current, item];
        });
      }

      if (item.kind === "insight") {
        if (!isCustomInsightsEnabled) {
          return;
        }
        setTaggedInsights((current) => {
          if (current.some((existing) => existing.id === item.id)) {
            return current;
          }
          return [...current, item];
        });
      }

      const nextCaret = queryInfo ? queryInfo.start : caretIndex;
      requestAnimationFrame(() => {
        textareaRef.current?.setSelectionRange(nextCaret, nextCaret);
        textareaRef.current?.focus();
      });
    },
    [
      adjustHeight,
      input,
      mentionEnd,
      mentionStart,
      selectedMentions.length,
      setInput,
      isCustomInsightsEnabled,
    ]
  );

  const handleMentionRemove = useCallback((item: MentionItem) => {
    if (item.kind === "dash") {
      setTaggedDashboards((current) =>
        current.filter((existing) => existing.id !== item.id)
      );
    }

    if (item.kind === "insight") {
      setTaggedInsights((current) =>
        current.filter((existing) => existing.id !== item.id)
      );
    }

    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setMentionEnd(null);
  }, []);

  const hasPendingAskUserQuestion = Boolean(pendingAskUserQuestion);

  return (
    <div className="relative w-full flex flex-col gap-4">
      {showSuggestedActions &&
        messages.length === 0 &&
        !hasPendingAskUserQuestion && (
          <SuggestedActions
            sendMessage={sendMessage}
            runtimeContextTextPart={runtimeContextTextPart}
          />
        )}

      <div
        ref={inputContainerRef}
        className={cx(
          "relative rounded-2xl border border-input bg-card flex flex-col p-2",
          className
        )}
      >
        {hasPendingAskUserQuestion && pendingAskUserQuestion ? (
          <AskUserQuestionPanel
            question={pendingAskUserQuestion}
            isSubmitting={isPendingAskUserQuestionSubmitting}
            onSubmit={(payload) => onSubmitAskUserQuestion?.(payload)}
          />
        ) : (
          <>
            {selectedMentions.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {selectedMentions.map((item) => (
                  <FilterChip
                    key={`${item.kind}-${item.id}`}
                    title={
                      <span className="text-xs font-medium">{item.label}</span>
                    }
                    icon={<XMarkIcon className="size-3" />}
                    onClick={() => textareaRef.current?.focus()}
                    onDelete={() => handleMentionRemove(item)}
                    isActive={false}
                    className="bg-background hover:bg-background/90"
                  />
                ))}
              </div>
            )}

            <textarea
              id="tracify-chat-input"
              data-testid="multimodal-input"
              ref={textareaRef}
              placeholder="Send a message..."
              value={input}
              autoFocus={true}
              onChange={handleInput}
              className={cx(
                "min-h-[24px] resize-none px-1 border-0 bg-transparent text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
              )}
              rows={2}
              onClick={(event) => {
                updateMentionState(
                  event.currentTarget.value,
                  event.currentTarget.selectionStart ??
                    event.currentTarget.value.length
                );
              }}
              onKeyUp={(event) => {
                if (!CARET_KEYS.has(event.key)) return;
                updateMentionState(
                  event.currentTarget.value,
                  event.currentTarget.selectionStart ??
                    event.currentTarget.value.length
                );
              }}
              onKeyDown={(event) => {
                if (mentionOpen) {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setMentionOpen(false);
                    return;
                  }

                  if (filteredMentions.length > 0) {
                    if (event.key === "ArrowDown") {
                      event.preventDefault();
                      setMentionActiveIndex(
                        (index) => (index + 1) % filteredMentions.length
                      );
                      return;
                    }

                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      setMentionActiveIndex(
                        (index) =>
                          (index - 1 + filteredMentions.length) %
                          filteredMentions.length
                      );
                      return;
                    }

                    if (event.key === "Enter") {
                      event.preventDefault();
                      const active = filteredMentions[safeMentionActiveIndex];
                      if (active) {
                        handleMentionSelect(active);
                      }
                      return;
                    }
                  }
                }

                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing
                ) {
                  event.preventDefault();

                  if (status !== "ready") {
                    toast.error(
                      "Please wait for the model to finish its response!"
                    );
                  } else {
                    submitForm();
                  }
                }
              }}
            />

            <Popover
              open={mentionOpen}
              modal={false}
              onOpenChange={(open) => {
                if (!open) {
                  setMentionOpen(false);
                  setMentionQuery("");
                  setMentionMode("textarea");
                  setMentionStart(null);
                  setMentionEnd(null);
                }
              }}
            >
              <PopoverAnchor asChild>
                <div className="absolute left-3 top-3 h-0 w-0" />
              </PopoverAnchor>
              <PopoverContent
                align="start"
                side="top"
                sideOffset={8}
                className="w-[min(420px,calc(100vw-2rem))] p-0"
                onOpenAutoFocus={(event) => {
                  event.preventDefault();
                  requestAnimationFrame(() => {
                    if (mentionMode === "command") {
                      commandInputRef.current?.focus();
                    } else {
                      textareaRef.current?.focus();
                    }
                  });
                }}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                  requestAnimationFrame(() => textareaRef.current?.focus());
                }}
                onInteractOutside={(event) => {
                  const target = event.target as Node | null;
                  if (target && textareaRef.current?.contains(target)) {
                    event.preventDefault();
                  }
                }}
              >
                <Command shouldFilter={false}>
                  {mentionMode === "command" && (
                    <CommandInput
                      ref={commandInputRef}
                      value={mentionQuery}
                      placeholder="Search insights or dashboards..."
                      onValueChange={(value) => {
                        setMentionQuery(value);
                        setMentionActiveIndex(0);
                      }}
                    />
                  )}
                  <CommandList className="max-h-[240px]">
                    {filteredMentions.length === 0 && (
                      <CommandEmpty>No matches.</CommandEmpty>
                    )}
                    {filteredDashMentions.length > 0 && (
                      <CommandGroup heading="Dashboards">
                        {filteredDashMentions.map((item) => (
                          <CommandItem
                            key={`${item.kind}-${item.id}`}
                            onSelect={() => handleMentionSelect(item)}
                            onMouseEnter={() =>
                              setMentionActiveIndex(item.index)
                            }
                            className={cx(
                              "flex items-center justify-between",
                              mentionMode !== "command" &&
                                safeMentionActiveIndex === item.index &&
                                "bg-accent/70 text-accent-foreground",
                              mentionMode !== "command" &&
                                "data-[selected=true]:bg-transparent data-[selected=true]:text-foreground"
                            )}
                          >
                            <span className="truncate">{item.label}</span>
                            <span className="text-xs text-muted-foreground">
                              @{item.kind}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                    {filteredInsightMentions.length > 0 && (
                      <CommandGroup heading="Insights">
                        {filteredInsightMentions.map((item) => (
                          <CommandItem
                            key={`${item.kind}-${item.id}`}
                            onSelect={() => handleMentionSelect(item)}
                            onMouseEnter={() =>
                              setMentionActiveIndex(item.index)
                            }
                            className={cx(
                              "flex items-center justify-between",
                              mentionMode !== "command" &&
                                safeMentionActiveIndex === item.index &&
                                "bg-accent/70 text-accent-foreground",
                              mentionMode !== "command" &&
                                "data-[selected=true]:bg-transparent data-[selected=true]:text-foreground"
                            )}
                          >
                            <span className="truncate">{item.label}</span>
                            <span className="text-xs text-muted-foreground">
                              @{item.kind}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <div className="flex justify-between items-center gap-2">
              <Button
                data-testid="chat-add-context"
                size="icon-sm"
                variant="ghost"
                className="text-muted-foreground hover:text-foreground"
                onClick={(event) => {
                  event.preventDefault();
                  openMentionPicker();
                }}
                aria-label="Add context"
                title="Add insight or dashboard"
              >
                <PlusIcon className="size-4" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-px">
                  <ModelSelector />
                  <ThinkingEffortSelector />
                </div>

                {status === "submitted" || status === "streaming" ? (
                  <StopButton stop={stop} setMessages={setMessages} />
                ) : (
                  <SendButton input={input} submitForm={submitForm} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const MultimodalInput = memo(
  PureMultimodalInput,
  (prevProps, nextProps) => {
    if (prevProps.input !== nextProps.input) return false;
    if (prevProps.status !== nextProps.status) return false;
    if (prevProps.showSuggestedActions !== nextProps.showSuggestedActions)
      return false;
    if (!equal(prevProps.seedTags, nextProps.seedTags)) return false;
    if (prevProps.seedInput !== nextProps.seedInput) return false;
    if (!equal(prevProps.runtimeContext, nextProps.runtimeContext))
      return false;
    if (
      !equal(prevProps.pendingAskUserQuestion, nextProps.pendingAskUserQuestion)
    )
      return false;
    if (
      prevProps.isPendingAskUserQuestionSubmitting !==
      nextProps.isPendingAskUserQuestionSubmitting
    )
      return false;

    return true;
  }
);

function PureStopButton({
  stop,
  setMessages,
}: {
  stop: () => void;
  setMessages: UseChatHelpers<ChatMessage>["setMessages"];
}) {
  return (
    <Button
      size="icon-sm"
      data-testid="stop-button"
      variant="muted"
      onClick={(event) => {
        event.preventDefault();
        stop();
        setMessages((messages) => messages);
      }}
    >
      <StopIcon className="size-4" />
    </Button>
  );
}

const StopButton = memo(PureStopButton);

function PureSendButton({
  submitForm,
  input,
}: {
  submitForm: () => void;
  input: string;
}) {
  return (
    <Button
      data-testid="send-button"
      size="icon-sm"
      onClick={(event) => {
        event.preventDefault();
        submitForm();
      }}
      disabled={input.length === 0}
    >
      <ArrowUpIcon className="size-4" />
    </Button>
  );
}

const SendButton = memo(PureSendButton, (prevProps, nextProps) => {
  if (prevProps.input !== nextProps.input) return false;
  return true;
});
